/**
 * Browser-side image compression.
 *
 * Aggressively shrinks images before upload so that:
 *  - the user's mobile data plan is preserved
 *  - object storage quota is saved
 *  - <img src> loads faster on the public site
 *
 * Behaviour:
 *  - Files that are already small (< COMPRESS_THRESHOLD_BYTES) are returned untouched.
 *  - Non-image files are returned untouched.
 *  - iPhone HEIC/HEIF files are transparently converted to JPEG first (Chrome and
 *    Firefox cannot decode HEIC natively).
 *  - Any decode/encode failure falls back to the original file — the upload still works.
 */

// Threshold: don't waste CPU on small files.
const COMPRESS_THRESHOLD_BYTES = 500 * 1024; // 500 KB
// Cap the longest edge to this many pixels. Enough for retina viewing.
const MAX_LONGEST_EDGE = 2000;
// JPEG quality (0-1). 0.85 is the sweet spot for photos.
const JPEG_QUALITY = 0.85;

const isImage = (file) => file && typeof file.type === "string" && file.type.startsWith("image/");

const isHeic = (file) => {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return (
    type === "image/heic" ||
    type === "image/heif" ||
    type === "image/heic-sequence" ||
    type === "image/heif-sequence" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
};

/**
 * Convert an iPhone HEIC/HEIF file to a JPEG File.
 * heic2any is loaded lazily so users who never upload HEIC don't pay the ~1MB cost.
 * Falls back to the original file if conversion fails (unsupported browser, corrupt file...).
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
const convertHeicToJpeg = async (file) => {
  try {
    const { default: heic2any } = await import("heic2any");
    const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
    // heic2any may return a single Blob or an array (for multi-image HEIC) — take the first.
    const blob = Array.isArray(converted) ? converted[0] : converted;
    if (!blob) return file;
    const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg") || "photo.jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    // Conversion failed — return original so the upload still has a chance.
    return file;
  }
};

/**
 * Decodes a File into an HTMLImageElement.
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
const decodeFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Décodage image impossible"));
    };
    img.src = url;
  });

/**
 * Compress a single File. Returns a new File (or the original if no compression is needed / failed).
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImage(file) {
  if (!file) return file;

  // Step 1 — HEIC/HEIF from iPhone: convert to JPEG first (all browsers can then decode it).
  let workingFile = file;
  if (isHeic(file)) {
    workingFile = await convertHeicToJpeg(file);
  }

  if (!isImage(workingFile)) return workingFile;
  if (workingFile.size < COMPRESS_THRESHOLD_BYTES) return workingFile;

  try {
    const img = await decodeFile(workingFile);
    const { width, height } = img;
    const longest = Math.max(width, height);
    const scale = longest > MAX_LONGEST_EDGE ? MAX_LONGEST_EDGE / longest : 1;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, targetW, targetH);

    // We always output JPEG — best size/quality ratio for photos and universally supported.
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob) return workingFile;
    if (blob.size >= workingFile.size) {
      // The compressed version is bigger than the original (rare, tiny PNGs) — keep the original.
      return workingFile;
    }

    const newName = workingFile.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    // Any failure = fall back to whatever we have so the upload still succeeds.
    return workingFile;
  }
}
